import { FormField } from '@/components/templates/entity-form'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  customFieldInputType,
  stringValue,
  toggleCustomFieldChoice,
} from '@/lib/contacts/custom-field-values'
import type { CustomField, CustomFieldFormValue } from '@/lib/contacts/types'

export function CustomFieldFormControl({
  customField,
  value,
  onChange,
}: {
  customField: CustomField
  value: CustomFieldFormValue | undefined
  onChange: (value: CustomFieldFormValue) => void
}) {
  const id = `custom-field-${customField.id}`

  if (customField.field_type === 'checkbox') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={value === true}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <Label htmlFor={id} className="text-muted-foreground">
          {customField.label}
        </Label>
      </div>
    )
  }

  if (customField.field_type === 'single-select') {
    return (
      <FormField label={customField.label} htmlFor={id}>
        <Select value={stringValue(value)} onValueChange={onChange}>
          <SelectTrigger id={id}>
            <SelectValue placeholder="Select a value" />
          </SelectTrigger>
          <SelectContent>
            {customField.choices.map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    )
  }

  if (customField.field_type === 'multi-select') {
    const selected = Array.isArray(value) ? value : []

    return (
      <div className="flex flex-col gap-2">
        <span className="text-body-sm text-foreground">{customField.label}</span>
        {customField.choices.map((choice) => {
          const choiceId = `${id}-${choice.toLowerCase().replace(/\s+/g, '-')}`
          return (
            <div key={choice} className="flex items-center gap-2">
              <Checkbox
                id={choiceId}
                checked={selected.includes(choice)}
                onCheckedChange={(checked) =>
                  onChange(toggleCustomFieldChoice(selected, choice, checked))
                }
              />
              <Label htmlFor={choiceId} className="text-muted-foreground">
                {choice}
              </Label>
            </div>
          )
        })}
      </div>
    )
  }

  if (customField.field_type === 'multiline text') {
    return (
      <FormField label={customField.label} htmlFor={id}>
        <Textarea
          id={id}
          value={stringValue(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      </FormField>
    )
  }

  return (
    <FormField label={customField.label} htmlFor={id}>
      <Input
        id={id}
        type={customFieldInputType(customField.field_type)}
        value={stringValue(value)}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormField>
  )
}
